import { useCrossFlow } from '@atlassiansox/cross-flow-support';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { isDesktop } from '@trello/browser';
import { useUserAge } from '@trello/business-logic-react/member';

import { type JiraTemplateIdType } from '../JiraTemplateId.types';
import { type TouchpointSourceType } from '../TouchpointSourceType';
import { trelloCrossFlowOpen } from '../TrelloCrossFlow';
import { useHasEligibleWorkspaces } from './runtimeEligibilityChecks/useHasEligibleWorkspaces';
import { useHasNoAvailableJiraSites } from './runtimeEligibilityChecks/useHasNoAvailableJiraSites';
import { useIsCrossFlowMemberConfirmed } from './runtimeEligibilityChecks/useIsCrossFlowMemberConfirmed';
import type { CrossFlowExperience } from './isSourceTargetedByExperience';
import { type CrossFlowWorkspace } from './useEligibleWorkspacesForProvisioning';
import { useIsCrossFlowExperienceEligible } from './useIsCrossFlowExperienceEligible';

interface PaidUsersExperienceParams {
  source: TouchpointSourceType;
  workspaceId?: string;
  boardId?: string;
  hasValidAaSession: boolean;
  jiraTemplateId?: JiraTemplateIdType;
  workspaces: CrossFlowWorkspace[];
}

const EXPERIENCE_KEY = 'paidUsersExperience';

export const useCrossFlowForPaidUsersExperience = ({
  source,
  workspaceId,
  boardId,
  hasValidAaSession,
  jiraTemplateId,
  workspaces,
}: PaidUsersExperienceParams): {
  experience: CrossFlowExperience;
  isEligible: boolean;
  open: ReturnType<typeof trelloCrossFlowOpen>;
  getIsTargeted: () => boolean;
} => {
  const crossFlow = useCrossFlow();

  // 1. Check that the member is confirmed
  const { isEligible: isMemberConfirmed } = useIsCrossFlowMemberConfirmed();
  // 2. Check that the member has eligible workspaces
  const { isEligible: hasEligibleWorkspaces, eligibleWorkspaceOptions } =
    useHasEligibleWorkspaces({
      workspaceId,
      entitlementRequired: 'standardOrPremium',
      treatFreeTrialAsFree: true,
      workspaces,
    });
  // 3. Check that the user is not on desktop
  const isWeb = !isDesktop();

  const [
    isImmediateRuntimeEligibilityChecksPassed,
    setIsImmediateRuntimeEligibilityChecksPassed,
  ] = useState(false);

  // 4. Check that the member has no available sites
  const { isEligible: hasNoAvailableSites } = useHasNoAvailableJiraSites({
    doLoadAvailableSites: isImmediateRuntimeEligibilityChecksPassed,
  });

  // 5. Check that the user is more than 21 days old
  const signedUpMoreThan21DaysAgo = useUserAge() > 21;

  const experience = useIsCrossFlowExperienceEligible({
    experience: EXPERIENCE_KEY,
    source,
    eligibilityCheckResults: [
      crossFlow.isEnabled,
      signedUpMoreThan21DaysAgo,
      hasEligibleWorkspaces,
      isMemberConfirmed,
      isWeb,
    ],
    deferredEligibilityCheckResults: [hasNoAvailableSites],
  });

  useEffect(() => {
    // TODO ENGHEALTH-2180: baselined when the rule was enabled
    // oxlint-disable-next-line react/set-state-in-effect
    setIsImmediateRuntimeEligibilityChecksPassed(
      experience.isImmediateRuntimeEligibilityChecksPassed,
    );
  }, [experience.isImmediateRuntimeEligibilityChecksPassed]);

  const getIsTargeted = useCallback((): boolean => {
    return signedUpMoreThan21DaysAgo;
  }, [signedUpMoreThan21DaysAgo]);

  return useMemo(
    () => ({
      experience: experience.experience,
      isEligible: experience.isEligible,
      open: trelloCrossFlowOpen(
        crossFlow,
        source,
        eligibleWorkspaceOptions,
        hasValidAaSession,
        {
          workspaceId,
          boardId,
          jiraTemplateId,
        },
      ),
      getIsTargeted,
    }),
    [
      experience.experience,
      experience.isEligible,
      crossFlow,
      source,
      eligibleWorkspaceOptions,
      hasValidAaSession,
      workspaceId,
      boardId,
      jiraTemplateId,
      getIsTargeted,
    ],
  );
};
